import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import EmptyListUI from '../../../widgets/output/EmptyListUI'
import './ClientWiseSummary.css'

const parseAmount = (value) => {
  if (!value || value === '-') return 0
  const n = parseFloat(String(value).replace(/[₹,\s]/g, '').replace(/^\((.*)\)$/, '-$1'))
  return Number.isFinite(n) ? n : 0
}

const formatSum = (list) => {
  const total = (list || []).reduce((sum, t) => sum + parseAmount(t.totalAmountF4), 0)
  return total ? total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null
}

const hasAmount = (value) => value != null && value !== '' && value !== '-'

const AmountTd = ({ value, variant = 'default' }) => (
  <td className={`cws_amount cws_amount_${variant} ${hasAmount(value) ? 'cws_amount_set' : 'cws_amount_empty'}`}>
    {hasAmount(value) ? value : '–'}
  </td>
)

const DetailSection = ({ title, variant, items }) => {
  const rows = items.filter(([, amount]) => hasAmount(amount))
  if (!rows.length) return null
  return (
    <div className={`cws_detail_section cws_detail_${variant}`}>
      <h4 className='cws_detail_title'>{title}</h4>
      <table className='cws_detail_table'>
        <tbody>
          {rows.map(([label, amount]) =>
            <tr key={label}>
              <td className='cws_detail_label'>{label}</td>
              <td className='cws_detail_amount'>{amount}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const AccountDetail = ({ account }) => (
  <div className='cws_detail_grid'>
    <DetailSection
      title='Opening balance'
      variant='opening'
      items={[['DSRA', account.openingDSRAF4], ['Surplus', account.openingSurplusF4]]}
    />
    <DetailSection
      title='Inflow'
      variant='inflow'
      items={(account.inflowTxnSummaryList || []).map((t) => [t.label, t.totalAmountF4])}
    />
    <DetailSection
      title='Outflow'
      variant='outflow'
      items={(account.outflowTxnSummaryList || []).map((t) => [t.label, t.totalAmountF4])}
    />
    <DetailSection
      title='Closing balance'
      variant='closing'
      items={[['DSRA', account.closingDSRAF4], ['Surplus', account.closingSurplusF4]]}
    />
  </div>
)

const SUMMARY_COLS = 9

const ClientWiseSummaryUI = ({ accountSummaryList }) => {
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  const filteredAccounts = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return accountSummaryList
    return accountSummaryList.filter((a) => (a.accountName || '').toLowerCase().includes(term))
  }, [accountSummaryList, search])

  if (!accountSummaryList) return null

  if (!accountSummaryList.length) {
    return <EmptyListUI createEnabled={false} createDisabledMessageLabel='No accounts found for this period' />
  }

  const toggleExpand = (accountId) => {
    setExpandedId((prev) => (prev === accountId ? null : accountId))
  }

  return (
    <div className='su_cws'>
      <div className='cws_toolbar'>
        <input
          type='search'
          className='cws_search'
          placeholder='Search by account name…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <button type='button' className='cws_clear_search' onClick={() => setSearch('')}>Clear</button>}
      </div>

      <div className='cws_table_wrap'>
        <table className='cws_table cws_summary_table'>
          <thead>
            <tr className='cws_header_row'>
              <th className='cws_expand_col cws_th_base' />
              <th className='cws_sno_col cws_th_base'>S no.</th>
              <th className='cws_client_col cws_th_base'>Account name</th>
              <th className='cws_section_opening'>Opening DSRA</th>
              <th className='cws_section_opening'>Opening surplus</th>
              <th className='cws_section_inflow'>Total inflow</th>
              <th className='cws_section_outflow'>Total outflow</th>
              <th className='cws_section_closing'>Closing DSRA</th>
              <th className='cws_section_closing'>Closing surplus</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map((account, i) => {
              const expanded = expandedId === account.accountId
              return (
                <React.Fragment key={account.accountId || i}>
                  <tr
                    className={`cws_row ${expanded ? 'cws_row_expanded' : ''}`}
                    onClick={() => toggleExpand(account.accountId)}
                  >
                    <td className='cws_expand_col'>
                      <span className={`cws_chevron ${expanded ? 'cws_chevron_open' : ''}`} />
                    </td>
                    <td className='cws_sno_col'>{i + 1}</td>
                    <td className='cws_client_col'>
                      <Link
                        to={`/su/accounts/${account.accountId}`}
                        className='cws_account_link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {account.accountName}
                      </Link>
                    </td>
                    <AmountTd value={account.openingDSRAF4} variant='opening' />
                    <AmountTd value={account.openingSurplusF4} variant='opening' />
                    <AmountTd value={formatSum(account.inflowTxnSummaryList)} variant='inflow' />
                    <AmountTd value={formatSum(account.outflowTxnSummaryList)} variant='outflow' />
                    <AmountTd value={account.closingDSRAF4} variant='closing' />
                    <AmountTd value={account.closingSurplusF4} variant='closing' />
                  </tr>
                  {expanded &&
                    <tr className='cws_detail_row'>
                      <td colSpan={SUMMARY_COLS}>
                        <AccountDetail account={account} />
                      </td>
                    </tr>
                  }
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {!filteredAccounts.length && <p className='cws_no_results'>No accounts match your search.</p>}
    </div>
  )
}

export default ClientWiseSummaryUI
